import { useQuery } from '@tanstack/react-query';
import { visitsApi, type ClientIndicatorsResponse } from '@/api/visits';

const CACHE_KEY = 'client-indicators-cache';

function loadCache(): ClientIndicatorsResponse | undefined {
  try {
    const saved = localStorage.getItem(CACHE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return undefined;
}

function saveCache(data: ClientIndicatorsResponse) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch { /* ignore, e.g. quota exceeded */ }
}

/**
 * Hook for fetching client indicators (payment status, delegations, docs)
 * Uses stale-while-revalidate pattern:
 * - Shows cached data while fetching new data (no flickering)
 * - Data stays fresh for 24 hours
 * - No refetch on window focus (expensive query)
 * - Caches in localStorage to prevent list jump on page reload
 */
export function useClientIndicators(clientIds: number[]) {
  // Sort and dedupe for stable query key
  const stableIds = [...new Set(clientIds)].sort((a, b) => a - b);

  return useQuery<ClientIndicatorsResponse>({
    queryKey: ['client-indicators', stableIds],
    queryFn: async () => {
      const data = await visitsApi.getClientIndicators(stableIds);
      saveCache(data);
      return data;
    },
    // Stale-while-revalidate: data considered fresh for 24 hours
    staleTime: 24 * 60 * 60 * 1000,
    // Use previous react-query data if available, otherwise localStorage cache
    placeholderData: (previousData) => previousData ?? loadCache(),
    // Don't refetch on window focus - too expensive
    refetchOnWindowFocus: false,
    // Only fetch if we have client IDs
    enabled: stableIds.length > 0,
    // Longer cache time to survive across page navigations
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}
